/* oxlint-disable typescript/no-explicit-any, typescript/no-unsafe-return -- payload types are dependent on events */
import { type Channel, Socket } from 'phoenix';

export type Listener = (payload: any) => void;

interface QontoWebsocketArgs {
  websocketBaseURL: string;
  token: string;
  userId: string;
  legacyTrigger?: (event: string, payload: any) => void;
}

export interface QontoWebsocketOptions {
  global?: boolean;
}

declare global {
  interface Window {
    __qontoWebsocket?: QontoWebsocket;
  }
}

class QontoWebsocket {
  private token: string | null = null;
  private userId: string | null = null;
  private socket: Socket | null = null;
  private channel?: Channel;
  private globalChannel?: Channel;
  private globalListeners: Record<string, Listener[]> = {};
  private listeners: Record<string, Listener[]> = {};

  /**
   * Instantiate the socket connection, and listen to `user:{uuid}` and `global` channels
   * @param websocketBaseURL - websocket url, based on the current Env
   * @param token - websocket token retrieved from the user's session
   * @param userId - current user's id
   * @param legacyTrigger - compatibility callback for Ember services relying on Evented
   */
  connect({ websocketBaseURL, token, userId, legacyTrigger }: QontoWebsocketArgs) {
    if (this.token === token && this.userId === userId) {
      return;
    }

    this.token = token;
    this.userId = userId;

    this.socket = new Socket(`${websocketBaseURL}/socket`, { params: { token } });
    this.socket.connect();

    this.channel = this.socket.channel(`user:${userId}`);
    this.channel.join();

    this.globalChannel = this.socket.channel('global');
    this.globalChannel.join();

    this.globalChannel.onMessage = (event: string, payload: any) => {
      this.trigger(event, payload, true);
      return payload;
    };

    this.channel.onMessage = (event: string, payload: any) => {
      this.trigger(event, payload);
      legacyTrigger?.(event, payload);
      return payload;
    };
  }

  /**
   * Disconnect and reconnect socket
   */
  reconnect(args: QontoWebsocketArgs) {
    if (this.socket) {
      this.token = null;
      this.userId = null;
      this.socket.disconnect();
    }
    this.connect(args);
  }

  /**
   * Disconnect socket, leave all channels, and reset all listeners
   */
  disconnect() {
    this.token = null;
    this.userId = null;
    this.listeners = {};
    this.globalListeners = {};

    if (this.channel) {
      this.channel.leave();
    }

    if (this.globalChannel) {
      this.globalChannel.leave();
    }

    if (this.socket) {
      this.socket.disconnect();
    }
  }

  /**
   * Returns a promise that triggers on the next message for a specific event
   *
   * Example:
   * ```js
   * function handleUserAction() {
   *  await client.mutation()
   *  const payload = await qontoWebsocket.next('my-event')
   *  // handle logic post event here.
   * }
   * ```
   */
  // oxlint-disable-next-line require-await
  async next(event: string, options?: QontoWebsocketOptions) {
    return new Promise(resolve => {
      let unsubscribe: (() => void) | null = null;
      unsubscribe = this.subscribe(
        event,
        payload => {
          unsubscribe?.();
          resolve(payload);
        },
        options
      );
    });
  }

  /**
   * ⚠️Prefer `useQontoWebsocket` when used in a React context.
   *
   * Listen to any given event and triggers the listener everytime a matching event is received.
   * @param event - name of the event
   * @param listener - callback handling the event
   * @param options - use `{ global: true }` if you want to listen for events on the global channel
   * @returns unsubscribe function
   */
  subscribe(event: string, listener: Listener, options?: QontoWebsocketOptions) {
    const listeners = options?.global ? this.globalListeners : this.listeners;

    if (!listeners[event]) {
      listeners[event] = [];
    }

    listeners[event].push(listener);

    return () => {
      if (listeners[event]) {
        listeners[event] = listeners[event].filter(l => l !== listener);
      }
    };
  }

  /**
   * Manually trigger events with a given payload.
   * Use this to mock events in tests.
   */
  trigger(event: string, payload: any, global?: boolean): void {
    const listeners = global ? this.globalListeners : this.listeners;

    if (listeners[event]) {
      listeners[event].forEach(l => {
        l(payload);
      });
    }
  }
}

export const qontoWebsocket = new QontoWebsocket();

if (typeof window !== 'undefined') {
  window.__qontoWebsocket = qontoWebsocket;
}
